/**
 * Compliance Issues Log: the words the screen uses and the small date helpers
 * behind them. Kept free of React so the download and the day counts can be
 * checked on their own.
 */

export const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  closed: 'Closed'
};

export const SOURCE_LABELS = {
  internal_audit: 'Internal audit',
  monitoring: 'Monitoring',
  self_identified: 'Self identified',
  disclosed_to_cms: 'Disclosed to CMS',
  other: 'Other'
};

export function statusLabel(code) {
  return STATUS_LABELS[code] || (code ? String(code).replace(/_/g, ' ') : '');
}

export function sourceLabel(code) {
  return SOURCE_LABELS[code] || (code ? String(code).replace(/_/g, ' ') : '');
}

export function isClosed(issue) {
  return issue?.status === 'closed';
}

export function pertainsTo(issue) {
  if (!issue) return 'Internal';
  return (issue.entity_kind === 'fdr' || issue.fdr_entity_id) ? 'FDR' : 'Internal';
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CMS_DATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;
// A DATE column arrives from the server as midnight UTC ("2026-07-21T00:00:00.000Z").
// It is a calendar day, not an instant: read it as the day it names, or it
// renders a day early anywhere west of Greenwich.
const UTC_MIDNIGHT_RE = /^(\d{4}-\d{2}-\d{2})T00:00:00(\.\d+)?Z$/;
const pad2 = (n) => String(n).padStart(2, '0');

function calendarPart(s) {
  if (ISO_DATE_RE.test(s)) return s;
  if (CMS_DATE_RE.test(s)) return s.replaceAll('/', '-');
  const m = UTC_MIDNIGHT_RE.exec(s);
  return m ? m[1] : null;
}

/** Any stored date or timestamp shown the CMS way: YYYY/MM/DD. */
export function cmsDate(value) {
  if (!value) return '';
  const s = String(value).trim();
  const day = calendarPart(s);
  if (day) return day.replaceAll('-', '/');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
}

/** A calendar date read as a local day; a real timestamp read as the instant it is. */
function asDay(value) {
  if (!value) return null;
  const s = String(value).trim();
  const day = calendarPart(s);
  if (day) return new Date(`${day}T00:00:00`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days from the day the issue was identified (or logged) to the day it closed. */
export function daysOpen(issue) {
  if (!issue) return null;
  const start = asDay(issue.date_issue_identified) || asDay(issue.created_at);
  const end = asDay(issue.closed_at);
  if (!start || !end) return null;
  const ms = end.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round(ms / 86400000));
}

export function closedYear(issue) {
  const d = asDay(issue?.closed_at);
  return d ? d.getFullYear() : null;
}

export function median(numbers) {
  const xs = numbers.filter((n) => typeof n === 'number' && !Number.isNaN(n)).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : Math.round((xs[mid - 1] + xs[mid]) / 2);
}

/**
 * The rows of the closed issues download, in the order of the CMS Pre-Audit
 * Issue Summary, followed by the record columns the log adds. `full` is false
 * for department users, who never see the disclosure columns.
 */
export function closedIssueRows(issues, { full = true } = {}) {
  const header = [
    'Issue #',
    'Program area impacted',
    'Issue description',
    'Root cause analysis',
    'Member impact (Y/N)',
    'Enrollees impacted',
    'Date issue identified',
    ...(full ? ['Date disclosed to CMS', 'Disclosed to (name)', 'Fully remediated (Y/N)'] : []),
    'System/operational remediation description',
    'Remediation started',
    'Remediation completed',
    'Pertains to',
    'Department',
    'Accountable owner',
    'Source',
    'Date closed',
    'Closed by',
    'Days open'
  ];
  const rows = (issues || []).map((i) => [
    i.issue_number || (i.id ? String(i.id).slice(0, 8) : ''),
    i.program_area_impacted || '',
    i.issue_description || '',
    i.root_cause_analysis || '',
    i.member_impact || '',
    i.enrollees_impacted ?? '',
    cmsDate(i.date_issue_identified),
    ...(full ? [cmsDate(i.date_issue_disclosed_to_cms), i.disclosed_to_name || '', i.fully_remediated_flag || ''] : []),
    i.system_remediation_description || '',
    cmsDate(i.system_remediation_started_at),
    cmsDate(i.system_remediation_completed_at),
    pertainsTo(i) === 'FDR' ? `FDR${i.fdr_entity_name ? ': ' + i.fdr_entity_name : ''}` : 'Internal',
    i.department_names || i.department_name || '',
    i.accountable_owner_names || i.accountable_owner_name || '',
    sourceLabel(i.source),
    cmsDate(i.closed_at),
    i.closed_by_name || '',
    daysOpen(i) ?? ''
  ]);
  return [header, ...rows];
}

export function csvText(rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(esc).join(',')).join('\r\n');
}
